// BUILD-042 — My HR visual pass (2026-10-05, Owner-directed): "need the ability to click on the
// profile image — it should pull up a box that gives the ability to change/upload/new profile
// image." No photo-upload backend exists yet (see MyHrNav.tsx's own note on the initials
// placeholder), so this stores the chosen image as a data URL in the CALLER's local component
// state only — nothing here calls a write API, and the chosen picture resets on page reload.
// Layout follows the Owner-provided reference screenshot: a centered circular preview, a small
// round "+" upload badge over its bottom-right edge, accepted-format/size/dimension hint text
// below, and Save/Cancel actions.
import { useRef, useState } from 'react';

const MAX_BYTES = 1024 * 1024; // 1MB, matching the reference screenshot's stated limit
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

export function ChangeProfilePictureModal({
  currentPhoto, onSave, onClose,
}: {
  currentPhoto: string | null;
  onSave: (dataUrl: string | null) => void;
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<string | null>(currentPhoto);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Please choose a JPG, PNG, or GIF image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('That image is larger than 1MB. Please choose a smaller file.');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  function handleSave() {
    onSave(preview);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-profile-picture-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg border border-gray-200 shadow-lg w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-gray-100">
          <h2 id="change-profile-picture-title" className="text-sm font-semibold text-gray-700">Change Profile Picture</h2>
        </div>

        <div className="px-5 py-8 flex flex-col items-center">
          <div className="relative">
            {preview ? (
              <img src={preview} alt="Profile preview" className="w-32 h-32 rounded-full object-cover border border-gray-200" />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
                <svg className="w-16 h-16 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5Zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5Z" />
                </svg>
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Upload a new profile picture"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#F0A33C] hover:bg-[#DD8F28] text-white flex items-center justify-center shadow-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          <p className="text-xs text-gray-400 mt-4 text-center">
            Accepts jpg, png, gif up to 1MB. Recommended dimensions 200px x 200px.
          </p>
          {error && <p className="text-xs text-red-700 mt-2 text-center">{error}</p>}
          {preview && (
            <button type="button" onClick={() => setPreview(null)} className="text-xs text-red-600 mt-2 hover:underline">
              Remove picture
            </button>
          )}
        </div>

        <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-gray-600 px-4 py-1.5 rounded hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="bg-[#6FA82C] hover:bg-[#5F9124] text-white text-sm font-medium px-5 py-1.5 rounded-full">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
